import React, { useState, useMemo, useEffect, useLayoutEffect } from "react";
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Filter,
  Calendar,
  User,
  MessageSquare,
  ChevronDown,
  Search,
  X,
  ExternalLink,
} from "lucide-react";

const SafeguardingViolationsDashboard = () => {
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedViolation, setExpandedViolation] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [preserveScrollY, setPreserveScrollY] = useState(null);
  const [currentVideo, setCurrentVideo] = useState({
    videoId: null,
    timestamp: null,
    title: "",
  });

  // Format timestamp in seconds to friendly format (HH:MM:SS)
  const formatTimestamp = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    }
  };

  console.log("🔄 Component render:", {
    isLoading,
    rawDataLength: rawData.length,
    selectedSeverity,
    searchTerm,
    expandedViolation,
  });

  // Load data from JSON file
  useEffect(() => {
    console.log("🚀 Initial data loading effect triggered");
    const loadData = async () => {
      try {
        console.log("📡 Fetching ./data.json...");
        const response = await fetch("./data.json");
        const jsonData = await response.json();
        console.log("✅ Data loaded successfully:", jsonData.length, "items");
        console.log("📊 First item sample:", jsonData[0]);
        setRawData(jsonData);
        setIsLoading(false);
        console.log("🔄 Set rawData and isLoading=false");
      } catch (error) {
        console.error("❌ Error loading data:", error);
        setRawData([]);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Force re-render after data loads when selectedSeverity is 'all'
  useEffect(() => {
    console.log("🔄 Re-render effect triggered:", {
      isLoading,
      rawDataLength: rawData.length,
      selectedSeverity,
    });
    if (!isLoading && rawData.length > 0 && selectedSeverity === "all") {
      console.log("🎯 Forcing re-render for selectedSeverity=all");
      setSelectedSeverity("all");
    }
  }, [isLoading, rawData.length]);

  // Preserve scroll position when toggling expanded/collapsed state or jumping to video
  useLayoutEffect(() => {
    if (preserveScrollY !== null) {
      window.scrollTo(0, preserveScrollY);
      setPreserveScrollY(null);
    }
  }, [expandedViolation, currentVideo.timestamp, preserveScrollY]);

  const violations = useMemo(() => {
    console.log(
      "⚙️ violations useMemo called with rawData.length:",
      rawData.length
    );
    if (!rawData || rawData.length === 0) {
      console.log("❌ No rawData, returning empty violations array");
      return [];
    }

    const result = rawData.map((item) => {
      const students = item.users.filter((u) => u.role === "Student");
      const teachers = item.users.filter(
        (u) => u.role === "Teacher" || u.role === "Guest"
      );

      // Use the safeguard_score from the JSON data directly
      const score = item.safeguard_score || 0;

      // Extract video ID from Vimeo URL
      const videoId = item.recording_link
        ? item.recording_link.match(/vimeo\.com\/(\d+)/)?.[1]
        : null;

      return {
        id: item.recording_id,
        sessionId: item.recording_id.slice(-6),
        recordingId: item.recording_id,
        students: students,
        teachers: teachers,
        date: new Date(item.timestamp),
        score: Math.round(score * 10) / 10,
        summary: item.summary,
        occurrences: item.safeguarding_evaluation_2 || [],
        spaceName: item.space_name,
        recordingLink: item.recording_link,
        videoId: videoId,
        category:
          score === 10
            ? "Critical"
            : score >= 9
            ? "High"
            : score >= 7
            ? "Medium"
            : "Low",
      };
    });

    console.log("✅ violations processed:", result.length, "items");
    console.log(
      "📊 Sample violation scores:",
      result.slice(0, 3).map((v) => ({ id: v.sessionId, score: v.score }))
    );
    return result;
  }, [rawData]);

  // AGGRESSIVE VIDEO MANAGEMENT: Ensure video state is always correct for the expanded violation
  useEffect(() => {
    if (expandedViolation && violations.length > 0) {
      const currentViolation = violations.find(
        (v) => v.id === expandedViolation
      );
      if (
        currentViolation &&
        currentVideo.videoId !== currentViolation.videoId
      ) {
        console.log(
          "🎬 Auto-correcting video state for expanded violation:",
          currentViolation.sessionId
        );
        setCurrentVideo({
          videoId: currentViolation.videoId,
          timestamp: null,
          title: "",
        });
      }
    } else if (!expandedViolation) {
      // Clear video state when no violation is expanded
      setCurrentVideo({
        videoId: null,
        timestamp: null,
        title: "",
      });
    }
  }, [expandedViolation, violations, currentVideo.videoId]);

  const getSeverityColor = (score) => {
    if (score === 10) return "text-red-700 bg-red-100 border-red-300";
    if (score >= 9) return "text-orange-700 bg-orange-100 border-orange-300";
    if (score >= 7) return "text-yellow-700 bg-yellow-100 border-yellow-300";
    return "text-blue-700 bg-blue-100 border-blue-300";
  };

  const getSeverityIcon = (score) => {
    if (score === 10) return "🚨";
    if (score >= 9) return "⚠️";
    if (score >= 7) return "⚡";
    return "ℹ️";
  };

  const filteredViolations = useMemo(() => {
    console.log("🔍 filteredViolations useMemo called:");
    console.log("  - violations.length:", violations.length);
    console.log("  - selectedSeverity:", selectedSeverity);
    console.log("  - searchTerm:", searchTerm);

    if (violations.length === 0) {
      console.log("❌ No violations to filter, returning empty array");
      return [];
    }

    const result = violations.filter((violation) => {
      const matchesSeverity =
        selectedSeverity === "all" ||
        (selectedSeverity === "critical" && violation.score === 10) ||
        (selectedSeverity === "high" &&
          violation.score >= 9 &&
          violation.score < 10) ||
        (selectedSeverity === "medium" &&
          violation.score >= 7 &&
          violation.score < 9) ||
        (selectedSeverity === "low" && violation.score < 7);

      const matchesSearch =
        searchTerm === "" ||
        violation.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        violation.spaceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        violation.students.some((s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        violation.teachers.some((t) =>
          t.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matches = matchesSeverity && matchesSearch;
      if (!matches && selectedSeverity === "all") {
        console.log(
          '🚨 UNEXPECTED: violation failed to match "all" severity:',
          {
            id: violation.sessionId,
            score: violation.score,
            matchesSeverity,
            matchesSearch,
          }
        );
      }

      return matches;
    });

    console.log("✅ filteredViolations result:", result.length, "items");
    if (selectedSeverity === "all" && result.length !== violations.length) {
      console.log('🚨 PROBLEM: "all" filter should match all violations!');
      console.log("  violations.length:", violations.length);
      console.log("  filtered result.length:", result.length);
    }
    return result;
  }, [violations, selectedSeverity, searchTerm]);

  const stats = useMemo(() => {
    const total = violations.length;
    const critical = violations.filter((v) => v.score === 10).length;
    const high = violations.filter((v) => v.score >= 9 && v.score < 10).length;

    return { total, critical, high };
  }, [violations]);

  const StatCard = ({ title, value, color, icon, isSmall = false }) => (
    <div
      className={`bg-white rounded-lg p-4 shadow-sm border border-gray-100 ${
        isSmall ? "min-w-0" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p
            className={`font-medium text-gray-600 ${
              isSmall ? "text-xs" : "text-sm"
            }`}
          >
            {title}
          </p>
          <p
            className={`font-bold ${color} ${isSmall ? "text-xl" : "text-3xl"}`}
          >
            {value}
          </p>
        </div>
        <div className={`${isSmall ? "text-lg" : "text-3xl"}`}>{icon}</div>
      </div>
    </div>
  );

  const ViolationCard = ({ violation }) => (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="text-2xl">{getSeverityIcon(violation.score)}</div>
            <div>
              <h3 className="font-semibold text-gray-800">
                {violation.spaceName}
              </h3>
              <p className="text-sm text-gray-500">
                {violation.date.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <div
              className={`px-3 py-1 rounded-full text-sm border ${getSeverityColor(
                violation.score
              )}`}
            >
              Severity Score: {violation.score}/10
            </div>
            <a
              href={`https://my.pencilapp.com/recordings/${violation.recordingId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm border border-blue-300 hover:bg-blue-200 transition-colors flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              View Recording
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Students</p>
            <div className="space-y-1">
              {violation.students.map((student, idx) => (
                <p key={idx} className="text-sm font-medium text-gray-800">
                  {student.name}
                </p>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Teachers/Staff</p>
            <div className="space-y-1">
              {violation.teachers.map((teacher, idx) => (
                <p key={idx} className="text-sm font-medium text-gray-800">
                  {teacher.name}
                </p>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-700 line-clamp-3">
            {violation.summary}
          </p>
        </div>

        {violation.occurrences.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">
              Key Incidents ({violation.occurrences.length})
            </p>
            <div className="space-y-2">
              {violation.occurrences.slice(0, 2).map((occurrence, idx) => (
                <div key={idx} className="text-xs bg-gray-50 rounded p-2">
                  <span className="font-medium">
                    @{formatTimestamp(occurrence.occurrence)}:
                  </span>{" "}
                  {occurrence.description.slice(0, 100)}...
                </div>
              ))}
              {violation.occurrences.length > 2 && (
                <p className="text-xs text-gray-500">
                  +{violation.occurrences.length - 2} more incidents
                </p>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Save current scroll position to restore after re-render
            setPreserveScrollY(window.scrollY);
            const isExpanding = expandedViolation !== violation.id;
            console.log("🔍 View Full Details clicked:", {
              violationId: violation.id,
              sessionId: violation.sessionId,
              isExpanding,
              currentExpandedViolation: expandedViolation,
            });

            // AGGRESSIVE VIDEO MANAGEMENT: Reset video state when expanding a new violation
            if (isExpanding) {
              console.log(
                "🎬 Resetting video state for new session:",
                violation.sessionId
              );
              setCurrentVideo({
                videoId: violation.videoId,
                timestamp: null,
                title: "",
              });
            } else {
              // Clear video state when collapsing
              console.log("🎬 Clearing video state when collapsing session");
              setCurrentVideo({
                videoId: null,
                timestamp: null,
                title: "",
              });
            }

            setExpandedViolation(
              expandedViolation === violation.id ? null : violation.id
            );
          }}
          className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          {expandedViolation === violation.id
            ? "Show Less"
            : "View Full Details"}
        </button>

        {expandedViolation === violation.id && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">
                  Full Analysis
                </h4>
                <p className="text-sm text-gray-700">{violation.summary}</p>
              </div>

              {/* Video Embed Section */}
              {violation.recordingLink && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">
                    Session Recording
                    {currentVideo.title && (
                      <span className="ml-2 text-sm text-blue-600">
                        - {currentVideo.title}
                      </span>
                    )}
                  </h4>
                  <div
                    className="relative bg-gray-100 rounded-lg overflow-hidden"
                    style={{ height: "300px" }}
                  >
                    <iframe
                      key={`session-${violation.sessionId}-video-${
                        currentVideo.videoId || violation.videoId
                      }-${currentVideo.timestamp || "0"}`}
                      src={`https://player.vimeo.com/video/${
                        currentVideo.videoId || violation.videoId
                      }?autoplay=1${
                        currentVideo.timestamp
                          ? `#t=${currentVideo.timestamp}s`
                          : ""
                      }`}
                      width="100%"
                      height="300"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={`Recording for ${violation.spaceName}`}
                    />
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    <p>
                      💡 <strong>Tip:</strong> Use the timestamps below to jump
                      to specific incidents in the video
                    </p>
                    {currentVideo.timestamp && (
                      <p className="mt-1 text-blue-600">
                        🎬 Currently showing:{" "}
                        {formatTimestamp(currentVideo.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {violation.occurrences.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    All Incidents with Timestamps
                  </h4>
                  <div className="space-y-3">
                    {violation.occurrences.map((occurrence, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            Timestamp: {formatTimestamp(occurrence.occurrence)}
                          </span>
                          <div className="flex gap-2">
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {Math.round(occurrence.probability * 100)}%
                              confident
                            </span>
                            {violation.recordingLink && (
                              <button
                                onClick={() => {
                                  const timestamp = occurrence.occurrence;
                                  console.log(
                                    "🎬 Jumping to video timestamp:",
                                    timestamp,
                                    "for session:",
                                    violation.sessionId
                                  );

                                  // Preserve scroll position when jumping to video
                                  const currentScrollY = window.scrollY;
                                  setPreserveScrollY(currentScrollY);

                                  setCurrentVideo({
                                    videoId: violation.videoId,
                                    timestamp: timestamp,
                                    title: `Incident at ${formatTimestamp(
                                      timestamp
                                    )}`,
                                  });
                                }}
                                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                              >
                                🎬 Jump to Moment
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">
                          {occurrence.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-800">
              Safeguarding Violations Dashboard
            </h1>
          </div>
          <p className="text-gray-600">
            Monitor and analyze safety incidents across all tutoring sessions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Sessions Analysed"
            value="1,000"
            color="text-blue-600"
            icon="🔍"
          />
          <StatCard
            title="Total Violations"
            value={stats.total}
            color="text-gray-800"
            icon="📊"
          />
          <StatCard
            title="Critical (10/10)"
            value={stats.critical}
            color="text-red-600"
            icon="🚨"
          />
          <StatCard
            title="High Risk (9+/10)"
            value={stats.high}
            color="text-orange-600"
            icon="⚠️"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                Filters:
              </span>
            </div>

            <select
              value={selectedSeverity}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log(
                  "🎯 Severity filter changed from",
                  selectedSeverity,
                  "to",
                  newValue
                );
                setSelectedSeverity(newValue);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical (10/10)</option>
              <option value="high">High (9+/10)</option>
              <option value="medium">Medium (7-8/10)</option>
              <option value="low">Low (&lt;7/10)</option>
            </select>

            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search violations, teachers, students, sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Violations List */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              Violations ({filteredViolations.length})
            </h2>
            <div className="text-sm text-gray-500">
              Sorted by severity (highest first)
            </div>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Loading violations...
              </h3>
              <p className="text-gray-500">
                Please wait while we fetch the data
              </p>
            </div>
          ) : filteredViolations.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No violations found
              </h3>
              <p className="text-gray-500">
                No violations found (Only sessions with score {">"}8 are shown
                in this dashboard)
              </p>
            </div>
          ) : (
            <div className="grid gap-6">
              {filteredViolations
                .sort((a, b) => b.score - a.score)
                .map((violation) => (
                  <ViolationCard key={violation.id} violation={violation} />
                ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>
            Safeguarding system actively monitoring all sessions for student and
            teacher safety
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafeguardingViolationsDashboard;
